import { defineNuxtPlugin, useRuntimeConfig } from "nuxt/app";
import type { Router } from "vue-router";
import { H3Error } from "h3";
import {
  denyUrls as commonDenyUrls,
  ignoreErrors as commonIgnoreErrors,
} from "@chatfood/bug-reporter";
import { NoopService } from "../service/noop-service";
import { ClientService } from "../service/client-service";
import { ServerService } from "../service/server-service";
import type { BugReporterService } from "../types";

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const moduleConfig = config.public.bugReporter;

  const bugReporter: BugReporterService = !moduleConfig.enabled
    ? new NoopService()
    : import.meta.browser
      ? new ClientService()
      : new ServerService();

  bugReporter.reportInit({
    app: nuxtApp.vueApp,
    environment: moduleConfig.environment,
    release: `${moduleConfig.project}@${moduleConfig.releaseName}`,
    dsn: moduleConfig.dsn,
    router: nuxtApp.$router as Router,
    enableReplay: true,
    tracesSampleRate: 0.01,
    denyUrls: commonDenyUrls.concat([
      // Errors coming from Google Maps API library
      // Most of then failling to fetch data from their server
      /maps\.googleapis\.com\/maps\/api\/js/i,
      // 3rd-party tools that could be injected through GTM
      /consent\.cookiebot\.com/i,
    ]),
    ignoreErrors: commonIgnoreErrors.concat([
      // Avoid reporting faulty FB Pixel implementation using Google Tag Manager - out of our scope
      "fbq is not defined",
      "Can't find variable: fbq",

      // Exceptions thrown from Satellite Map that does not break ux
      "Google Maps Geocoder instance not found.",
      "Google Maps: Geocode service is not available.",
      "Maps instance not found.",
      "Google Maps API could not be loaded",

      // Geocode issues from Satellite - does not break ux
      // https://deliverect.sentry.io/issues/5598008058/events/23f5e6c1c46b4ea6af93289367ce74b0/
      "value.Geocoder",
      // https://deliverect.sentry.io/issues/5971763632/events/95c41931afeb4cfda1e5cf6a239eae99/
      "Cannot read properties of undefined (reading 'geocode')",

      // Geolocation errors - these are expected user interactions, not application bugs
      // User denied location permission, location unavailable, timeout, etc.
      "GeolocationPositionError",
      "User denied Geolocation",
      "Location information is unavailable",
      "The location acquisition process timed out",

      // Issues arise when older pods are active alongside newer ones, causing asset unavailability
      // A hard pod swap and efficient cachigng are needed to prevent downtime during deployment
      "Failed to fetch dynamically imported module",
      "Importing a module script failed",

      // This is a Google Search App issue on iOS
      // https://issuetracker.google.com/issues/396043331
      "Can't find variable: gmo",

      // Temporary ingore to avoid flood from bots
      // blocked by CloudFlare not yet covering OO
      "getPickupOutlets - Request failed with status code 403",
      "getMenu - Request failed with status code 403",
      "getMenus - Request failed with status code 403",
      "getItem - Request failed with status code 403",
      "getProviders - Request failed with status code 403",
      "getSettings - Request failed with status code 403",
      "getMedias - Request failed with status code 403",
      "getCustomerProfile - Request failed with status code 403",
      "getOutletById - Request failed with status code 403",
      "getOutletBySlug - Request failed with status code 403",
      "getCoverageForCoordinates - Request failed with status code 403",
      "getTimeSlots - Request failed with status code 403",
    ]),
    ignoreUserAgents: [
      // Common Google Read Aloud bots
      /Google-Read-Aloud/,
    ],
  });

  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    bugReporter.report(error, {
      context: {
        instance,
        info,
      },
    });
  };

  nuxtApp.hook("app:error", (err) => {
    if (err instanceof H3Error) {
      if ([404, 422].includes(err.statusCode)) {
        return;
      }
    }

    bugReporter.report(err);
  });

  return {
    provide: {
      bugReporter,
    },
  };
});
